import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, LogIn, AlertCircle, Clock, QrCode } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/common/Button';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { Logo } from '../components/common/Logo';
import { RankingGroupImage } from '../components/rankings/RankingGroupImage';
import { NamePrompt } from '../components/common/NamePrompt';
import { api } from '../services/api';
import type { RankingInvitePreview } from '../types';

function formatBRL(cents: number | null): string {
  return ((cents ?? 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** "12345678909" → "123.456.789-09" while typing. */
function maskCpf(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d{1,2})$/, '.$1-$2');
}

export function JoinRankingPage() {
  const navigate = useNavigate();
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const { isAuthenticated, isLoading: authLoading, refreshSubscriber, subscriber } = useAuth();

  const [ranking, setRanking] = useState<RankingInvitePreview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isJoining, setIsJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joinSuccess, setJoinSuccess] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [cpf, setCpf] = useState('');
  const [birthDate, setBirthDate] = useState('');

  const isPaid = !!ranking?.payment;
  const needsCpf = isPaid && (!subscriber?.cpf || !subscriber?.birthDate);
  // Standings hide nameless players — capture a display name before joining.
  const needsName = !subscriber?.name?.trim();

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      navigate(`/login?redirect=/rankings/join/${inviteCode}`, { replace: true });
      return;
    }

    loadPreview();
  }, [authLoading, isAuthenticated, inviteCode]);

  const loadPreview = async () => {
    if (!inviteCode) return;
    setIsLoading(true);

    try {
      const response = await api.rankings.invitePreview(inviteCode);
      if (response.success && response.data) {
        if (response.data.isMember) {
          navigate(`/rankings/${response.data.id}`, { replace: true });
          return;
        }
        setRanking(response.data);
      } else {
        setNotFound(true);
      }
    } catch {
      setNotFound(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!inviteCode) return;
    setIsJoining(true);
    setJoinError(null);

    try {
      // Paid ranking: make sure the subscriber has CPF + birth date first
      // (required by Asaas to create the charge).
      if (needsCpf) {
        const bareCpf = cpf.replace(/\D/g, '');
        if (bareCpf.length !== 11 || !birthDate) {
          setJoinError('Informe seu CPF e data de nascimento');
          setIsJoining(false);
          return;
        }
        const profileResponse = await api.auth.updateProfile({
          ...(subscriber?.cpf ? {} : { cpf: bareCpf }),
          ...(subscriber?.birthDate ? {} : { birthDate }),
        });
        if (!profileResponse.success) {
          setJoinError(profileResponse.error?.message || 'CPF inválido');
          setIsJoining(false);
          return;
        }
      }

      const response = await api.rankings.join(inviteCode);
      if (response.success && response.data) {
        await refreshSubscriber();
        if (response.data.status === 'pending_payment') {
          // Straight to the ranking page, which shows the PIX payment screen.
          navigate(`/rankings/${response.data.rankingId}`, { replace: true });
          return;
        }
        setJoinSuccess(true);
        setTimeout(() => {
          navigate(`/rankings/${response.data!.rankingId}`, { replace: true });
        }, 1200);
      } else {
        if (response.error?.code === 'ALREADY_MEMBER') {
          const rid = (response as any).data?.rankingId;
          if (rid) navigate(`/rankings/${rid}`, { replace: true });
          return;
        }
        setJoinError(response.error?.message || 'Erro ao entrar no ranking');
      }
    } catch {
      setJoinError('Erro de conexão');
    } finally {
      setIsJoining(false);
    }
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-deep-blue flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-deep-blue flex flex-col items-center justify-center p-6">
        <Logo size="sm" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mt-8 max-w-xs"
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-coral/10 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-coral" />
          </div>
          <h1 className="font-display text-xl font-bold text-white mb-2">
            Convite não encontrado
          </h1>
          <p className="text-white/50 text-sm mb-6">
            Este link de convite é inválido ou o ranking não existe mais.
          </p>
          <Button onClick={() => navigate('/rankings')} fullWidth>
            Ir para Rankings
          </Button>
        </motion.div>
      </div>
    );
  }

  if (joinSuccess && ranking) {
    return (
      <div className="min-h-screen bg-deep-blue flex flex-col items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-xs"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', delay: 0.2 }}
            className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-turquoise/10 flex items-center justify-center"
          >
            <Clock className="w-10 h-10 text-turquoise" />
          </motion.div>
          <h1 className="font-display text-xl font-bold text-white mb-2">
            Pedido enviado!
          </h1>
          <p className="text-white/50 text-sm">
            Aguardando aprovação do admin de {ranking.name}...
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-blue flex flex-col items-center justify-center p-6">
      <Link to="/">
        <Logo size="sm" />
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mt-8 max-w-xs w-full"
      >
        {ranking && (
          <>
            <div className="flex justify-center mb-4">
              <RankingGroupImage imageUrl={ranking.imageUrl} size="md" />
            </div>

            <h1 className="font-display text-2xl font-bold text-white mb-1">
              {ranking.name}
            </h1>

            {ranking.description && (
              <p className="text-white/40 text-sm mb-2 whitespace-pre-line">{ranking.description}</p>
            )}

            <p className="text-white/50 text-sm flex items-center justify-center gap-1.5 mb-4">
              <Users className="w-4 h-4" />
              {ranking.playerCount} jogador{ranking.playerCount !== 1 ? 'es' : ''}
            </p>

            {isPaid && (
              <div className="p-3 rounded-xl bg-turquoise/10 border border-turquoise/20 mb-4 flex items-center justify-center gap-2">
                <QrCode className="w-4 h-4 text-turquoise" />
                <p className="text-turquoise text-sm font-semibold">
                  Inscrição: {formatBRL(ranking.payment!.amountCents)} via PIX
                </p>
              </div>
            )}

            {ranking.status === 'finished' ? (
              <div className="p-4 rounded-xl bg-white/10 text-white/50 text-sm mb-4">
                Este ranking está encerrado.
              </div>
            ) : needsName ? (
              <NamePrompt subtitle={`Esse é o nome que os outros jogadores vão ver em ${ranking.name}.`} />
            ) : (
              <>
                {needsCpf && (
                  <div className="text-left space-y-3 mb-4">
                    <p className="text-white/50 text-xs">
                      Para gerar a cobrança PIX, precisamos do seu CPF e data de nascimento.
                    </p>
                    {!subscriber?.cpf && (
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="CPF (000.000.000-00)"
                        value={cpf}
                        onChange={(e) => setCpf(maskCpf(e.target.value))}
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 text-sm focus:outline-none focus:border-turquoise/50"
                      />
                    )}
                    {!subscriber?.birthDate && (
                      <input
                        type="date"
                        value={birthDate}
                        onChange={(e) => setBirthDate(e.target.value)}
                        max={new Date().toISOString().slice(0, 10)}
                        className="w-full min-w-0 min-h-[46px] appearance-none px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 text-sm text-left focus:outline-none focus:border-turquoise/50 [color-scheme:dark] [&::-webkit-date-and-time-value]:text-left"
                      />
                    )}
                  </div>
                )}
                <Button onClick={handleJoin} fullWidth isLoading={isJoining} size="md">
                  <LogIn className="w-5 h-5" />
                  {isPaid ? 'Entrar e pagar inscrição' : 'Pedir para entrar'}
                </Button>
              </>
            )}

            {joinError && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-coral text-sm mt-4"
              >
                {joinError}
              </motion.p>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
}
